/** A lookup that failed is said once, above the screen (E2E P9, G-P9-21).
 *
 * Two reads sit under dozens of screens without being theirs: **the roster** (`/members`, an Admin's,
 * which turns a person's identifier into a name) and **the profile** (`/profile`, which places a time
 * in the reader's zone). Each screen that lists people or times asks for them as a convenience, and
 * when one failed the screen quietly printed what it had: a column of UUIDs where names were, and
 * Coordinated Universal Time where the reader's own was. Nothing said so, so the change read as the
 * product's own choice.
 *
 * The screens now draw the plain fallback (`PersonRef`'s *Name not loaded*, the time's own zone
 * name) and **this component says why, once**, with a way to ask again. It reads the query cache and
 * never fetches: a screen that did not ask for a lookup has nothing to report, and a role that may
 * not read the roster never asked. A refusal (403) is an answer and not a failure, so it is not said.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";

import { profileKeys } from "../api/profile";
import { Refused } from "../api/refusal";
import { ReadsFailed } from "../components/Loaded";
import styles from "./LookupsFailed.module.css";

const LOOKUPS = [
  { key: ["members"] as const, of: "The names of the people in this workspace" },
  { key: profileKeys.profile(), of: "Your time zone and language" },
] as const;

export function LookupsFailed() {
  const client = useQueryClient();
  const cache = client.getQueryCache();
  // A string, so the snapshot is stable between renders that changed nothing.
  const snapshot = useSyncExternalStore(
    (notify) => cache.subscribe(notify),
    () =>
      LOOKUPS.map(({ key }) => {
        const state = client.getQueryState(key);
        const error = state?.status === "error" ? state.error : null;
        return error instanceof Refused && error.isUnauthorized ? "-" : error ? "x" : "-";
      }).join(""),
  );
  const failed = LOOKUPS.filter((_, at) => snapshot[at] === "x");
  if (failed.length === 0) return null;
  return (
    <div data-testid="lookups-failed" className={styles.strip}>
      <ReadsFailed
        reads={failed.map(({ key, of }) => ({
          of,
          read: {
            error: client.getQueryState(key)?.error ?? new Error("That could not be loaded."),
            isPending: false,
            refetch: () => client.refetchQueries({ queryKey: key, exact: true }),
          },
        }))}
      />
    </div>
  );
}
